import React, { useEffect, useState } from 'react';
import { getAllAds, addAd, updateAd, deleteAd } from '../../firebase/firestore';
import { uploadImage, deleteFile } from '../../firebase/storage';
import { formatDateTime } from '../../utils/dateHelpers';
import './ManageAds.css';

const EMPTY_FORM = { title: '', linkURL: '', adType: 'popup', isActive: false, startDate: '', endDate: '' };

export default function ManageAds() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    const data = await getAllAds();
    setAds(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImage = e => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile && !imagePreview) return setError('Please select an ad image');
    if (!form.title) return setError('Ad title is required');

    if (form.adType === 'popup' && form.isActive) {
      const activePopup = ads.find(a => a.adType === 'popup' && a.isActive);
      if (activePopup && !window.confirm('Another popup ad is already active. Only one popup can show at a time. Deactivate the existing one and activate this?')) return;
      if (activePopup) await updateAd(activePopup.id, { isActive: false });
    }

    setSaving(true);
    setError('');
    try {
      let imageURL = imagePreview;
      if (imageFile) imageURL = await uploadImage(imageFile, 'ads');
      await addAd({ ...form, imageURL });
      setSuccess('Advertisement added successfully!');
      setForm(EMPTY_FORM);
      setImageFile(null);
      setImagePreview('');
      load();
    } catch (err) {
      setError('Failed to save ad. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (ad) => {
    if (!ad.isActive && ad.adType === 'popup') {
      const activePopup = ads.find(a => a.adType === 'popup' && a.isActive && a.id !== ad.id);
      if (activePopup) await updateAd(activePopup.id, { isActive: false });
    }
    await updateAd(ad.id, { isActive: !ad.isActive });
    setAds(prev => prev.map(a => {
      if (a.id === ad.id) return { ...a, isActive: !a.isActive };
      if (!ad.isActive && ad.adType === 'popup' && a.adType === 'popup') return { ...a, isActive: false };
      return a;
    }));
  };

  const handleDelete = async (ad) => {
    if (!window.confirm('Delete this ad?')) return;
    if (ad.imageURL) await deleteFile(ad.imageURL).catch(() => {});
    await deleteAd(ad.id);
    setAds(prev => prev.filter(a => a.id !== ad.id));
  };

  const AD_TYPE_LABELS = { popup: '🎯 Popup', header: '📌 Header Banner', sidebar: '📋 Sidebar' };

  return (
    <div className="manage-ads">
      <div className="page-header-row">
        <h1 className="page-title">📢 Advertisement Management</h1>
      </div>

      <div className="ads-layout">
        <div className="ad-form-card">
          <h2 className="card-section-title">Add New Advertisement</h2>
          {error && <div className="form-error">{error}</div>}
          {success && <div className="form-success">{success}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Ad Title *</label>
              <input name="title" value={form.title} onChange={handleChange} placeholder="Summer Sale 2024" required />
            </div>
            <div className="form-group">
              <label>Ad Image *</label>
              <div className="file-dropzone" onClick={() => document.getElementById('ad-image').click()}>
                {imagePreview ? (
                  <div className="ad-preview">
                    <img src={imagePreview} alt="Ad preview" />
                    <button type="button" onClick={e => { e.stopPropagation(); setImageFile(null); setImagePreview(''); }} className="remove-file">✕</button>
                  </div>
                ) : (
                  <div className="dropzone-prompt">
                    <span className="dropzone-icon">🖼️</span>
                    <p>Click to upload ad image</p>
                    <span className="dropzone-hint">JPG, PNG, GIF recommended</span>
                  </div>
                )}
              </div>
              <input id="ad-image" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImage} />
            </div>
            <div className="form-group">
              <label>Click Destination URL</label>
              <input name="linkURL" value={form.linkURL} onChange={handleChange} placeholder="https://example.com/offer" type="url" />
            </div>
            <div className="form-group">
              <label>Ad Placement</label>
              <select name="adType" value={form.adType} onChange={handleChange}>
                <option value="popup">🎯 Popup (on page load)</option>
                <option value="header">📌 Header Banner (below navbar)</option>
                <option value="sidebar">📋 Sidebar Ad</option>
              </select>
            </div>
            <div className="form-grid-2">
              <div className="form-group">
                <label>Start Date</label>
                <input type="date" name="startDate" value={form.startDate} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input type="date" name="endDate" value={form.endDate} onChange={handleChange} />
              </div>
            </div>
            <div className="form-check">
              <input type="checkbox" id="isActive" name="isActive" checked={form.isActive} onChange={handleChange} />
              <label htmlFor="isActive">Activate immediately after adding</label>
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: '16px' }} disabled={saving}>
              {saving ? 'Saving...' : '📢 Add Advertisement'}
            </button>
          </form>
        </div>

        <div className="ads-list-card">
          <h2 className="card-section-title">All Advertisements ({ads.length})</h2>
          {loading ? <div className="loading-spinner">Loading...</div>
            : ads.length === 0
              ? <div className="empty-state"><h3>No ads yet</h3></div>
              : (
                <div className="ads-list">
                  {ads.map(ad => (
                    <div key={ad.id} className={`ad-item ${ad.isActive ? 'active' : ''}`}>
                      <div className="ad-item-image">
                        <img src={ad.imageURL} alt={ad.title} />
                      </div>
                      <div className="ad-item-info">
                        <div className="ad-item-title">{ad.title}</div>
                        <div className="ad-item-meta">
                          <span className="ad-type-badge">{AD_TYPE_LABELS[ad.adType]}</span>
                          <span>{formatDateTime(ad.createdAt)}</span>
                          {ad.linkURL && <a href={ad.linkURL} target="_blank" rel="noopener noreferrer" className="ad-link">🔗 Link</a>}
                        </div>
                      </div>
                      <div className="ad-item-actions">
                        <label className="toggle-switch">
                          <input type="checkbox" checked={ad.isActive} onChange={() => handleToggle(ad)} />
                          <span className="toggle-slider" />
                        </label>
                        <span className={`toggle-label ${ad.isActive ? 'on' : 'off'}`}>{ad.isActive ? 'Live' : 'Off'}</span>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(ad)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )
          }
        </div>
      </div>
    </div>
  );
}
